import { useState, useCallback } from 'react';
import { supabase } from './useSupabase';
import { showToast } from '@/components/Toast';

const DEFAULT_BUCKET = 'public-assets';
const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

export interface Asset {
  id: string;
  name: string;
  type: string;
  category?: string;
  storage_path: string;
  public_url: string;
  size_bytes?: number;
  mime_type?: string;
  preset_id?: string;
  created_at: string;
}

let cachedBucket: string | null = null;

export function useStorage() {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const discoverBucket = useCallback(async (): Promise<string | null> => {
    if (cachedBucket) return cachedBucket;

    const candidates = [DEFAULT_BUCKET, 'imagem', 'imagens', 'images', 'assets', 'media'];

    for (const bucketName of candidates) {
      try {
        const { error: listErr } = await supabase.storage.from(bucketName).list('', { limit: 1 });
        if (!listErr || listErr.message?.includes('empty') || listErr.message?.includes('The resource was not found')) {
          // Se não deu erro de bucket not found, o bucket existe
          if (!listErr || !listErr.message?.toLowerCase().includes('bucket') || !listErr.message?.toLowerCase().includes('not found')) {
            cachedBucket = bucketName;
            console.log('[Storage] Bucket detectado:', bucketName);
            return bucketName;
          }
        }
      } catch (e) {
        // ignora e tenta próximo
      }
    }

    return null;
  }, []);

  const ensureBucket = useCallback(async (): Promise<string | null> => {
    const existing = await discoverBucket();
    if (existing) return existing;

    try {
      // Tentativa via edge function
      const res = await fetch(
        `${SUPABASE_URL}/functions/v1/storage-setup`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
          },
        }
      );
      if (res.ok) {
        const json = await res.json();
        if (json.bucket) {
          cachedBucket = json.bucket;
          return json.bucket;
        }
      }
    } catch (e) {
      console.log('[Storage] Edge function exception:', e);
    }

    try {
      // Tentativa de criar diretamente
      const { data: bucketData, error: bucketError } = await supabase.storage.createBucket(DEFAULT_BUCKET, {
        public: true,
        fileSizeLimit: 10485760,
        allowedMimeTypes: ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'],
      });
      if (!bucketError && bucketData) {
        cachedBucket = DEFAULT_BUCKET;
        return DEFAULT_BUCKET;
      }
    } catch (e) {
      console.log('[Storage] createBucket exception:', e);
    }

    return null;
  }, [discoverBucket]);

  const uploadImage = useCallback(
    async (
      file: File,
      type: string,
      name?: string,
      category?: string,
      presetId?: string
    ): Promise<Asset | null> => {
      setUploading(true);
      setError(null);
      try {
        // Verificar tipo de arquivo
        const allowedTypes = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];
        if (!allowedTypes.includes(file.type)) {
          throw new Error(
            `Tipo de arquivo n\u00e3o permitido: ${file.type}. Use PNG, JPEG, WEBP ou SVG.`
          );
        }

        // Verificar tamanho (10MB)
        if (file.size > 10 * 1024 * 1024) {
          throw new Error('Arquivo muito grande. M\u00e1ximo 10MB.');
        }

        const fileName = `${Date.now()}-${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
        const path = `${type}/${fileName}`;

        // Descobrir/criar bucket
        const bucketName = await ensureBucket();
        if (!bucketName) {
          throw new Error(
            'Nenhum bucket de armazenamento dispon\u00edvel. Crie manualmente no painel do Supabase em Storage > New Bucket.'
          );
        }

        console.log('[Storage] Usando bucket:', bucketName);
        console.log('[Storage] Tentando upload em:', bucketName, path);

        // Tentar upload
        const { error: uploadError } = await supabase.storage
          .from(bucketName)
          .upload(path, file, {
            cacheControl: '3600',
            upsert: false,
          });

        if (uploadError) {
          console.log('[Storage] Upload falhou:', uploadError.message);
          throw uploadError;
        }

        console.log('[Storage] Upload OK!');

        const {
          data: { publicUrl },
        } = supabase.storage.from(bucketName).getPublicUrl(path);

        console.log('[Storage] Public URL:', publicUrl);

        const { data: inserted, error: dbError } = await supabase
          .from('assets_library')
          .insert({
            name: name || file.name,
            type,
            category: category || null,
            storage_path: path,
            public_url: publicUrl,
            size_bytes: file.size,
            mime_type: file.type,
            preset_id: presetId || null,
          })
          .select()
          .single();

        if (dbError) {
          console.log('[Storage] DB insert falhou:', dbError.message);
          throw dbError;
        }

        console.log('[Storage] Asset salvo no DB:', inserted?.id);
        return inserted as Asset;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Erro no upload';
        console.error('[Storage] Erro final:', msg);
        setError(msg);
        showToast('Erro ao enviar imagem: ' + msg, 'error');
        return null;
      } finally {
        setUploading(false);
      }
    },
    [ensureBucket]
  );

  const uploadImageByUrl = useCallback(
    async (
      url: string,
      type: string,
      name: string,
      category?: string,
      presetId?: string
    ): Promise<Asset | null> => {
      setUploading(true);
      setError(null);
      try {
        // Validar URL
        const urlObj = new URL(url);
        if (!urlObj.protocol.startsWith('http')) {
          throw new Error('URL inv\u00e1lida. Use http:// ou https://');
        }

        // Fazer fetch da imagem
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`N\u00e3o foi poss\u00edvel baixar a imagem: ${response.status}`);
        }
        const blob = await response.blob();

        // Detectar tipo
        const mimeType = blob.type || 'image/jpeg';
        const allowedTypes = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml', 'image/gif'];
        if (!allowedTypes.includes(mimeType)) {
          throw new Error(`Tipo de imagem n\u00e3o suportado: ${mimeType}`);
        }

        // Verificar tamanho
        if (blob.size > 10 * 1024 * 1024) {
          throw new Error('Imagem muito grande. M\u00e1ximo 10MB.');
        }

        const ext = mimeType === 'image/png' ? 'png' : mimeType === 'image/webp' ? 'webp' : 'jpg';
        const fileName = `${Date.now()}-url.${ext}`;
        const path = `${type}/${fileName}`;

        // Descobrir/criar bucket
        const bucketName = await ensureBucket();
        if (!bucketName) {
          throw new Error(
            'Nenhum bucket de armazenamento dispon\u00edvel. Crie manualmente no painel do Supabase.'
          );
        }

        console.log('[Storage] Upload por URL em bucket:', bucketName, path);

        const { error: uploadError } = await supabase.storage
          .from(bucketName)
          .upload(path, blob, {
            cacheControl: '3600',
            upsert: false,
            contentType: mimeType,
          });

        if (uploadError) {
          console.log('[Storage] Upload por URL falhou:', uploadError.message);
          throw uploadError;
        }

        const {
          data: { publicUrl },
        } = supabase.storage.from(bucketName).getPublicUrl(path);

        const { data: inserted, error: dbError } = await supabase
          .from('assets_library')
          .insert({
            name: name || 'Imagem URL',
            type,
            category: category || null,
            storage_path: path,
            public_url: publicUrl,
            size_bytes: blob.size,
            mime_type: mimeType,
            preset_id: presetId || null,
          })
          .select()
          .single();

        if (dbError) throw dbError;

        return inserted as Asset;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Erro no upload';
        setError(msg);
        showToast('Erro ao enviar imagem por URL: ' + msg, 'error');
        return null;
      } finally {
        setUploading(false);
      }
    },
    [ensureBucket]
  );

  const listAssets = useCallback(
    async (type?: string, category?: string): Promise<Asset[]> => {
      try {
        let query = supabase.from('assets_library').select('*').order('created_at', { ascending: false });
        if (type) query = query.eq('type', type);
        if (category) query = query.eq('category', category);
        const { data, error: dbError } = await query;
        if (dbError) throw dbError;
        return (data || []) as Asset[];
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao listar assets');
        showToast('Erro ao listar imagens: ' + (err instanceof Error ? err.message : 'Erro desconhecido'), 'error');
        return [];
      }
    },
    []
  );

  const deleteAsset = useCallback(async (id: string, storagePath: string): Promise<boolean> => {
    try {
      const bucketName = cachedBucket || DEFAULT_BUCKET;
      const { error: storageError } = await supabase.storage.from(bucketName).remove([storagePath]);
      if (storageError) throw storageError;

      const { error: dbError } = await supabase.from('assets_library').delete().eq('id', id);
      if (dbError) throw dbError;

      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao remover asset');
      showToast('Erro ao remover imagem: ' + (err instanceof Error ? err.message : 'Erro desconhecido'), 'error');
      return false;
    }
  }, []);

  const replaceAsset = useCallback(
    async (
      id: string,
      oldPath: string,
      file: File,
      name?: string
    ): Promise<Asset | null> => {
      setUploading(true);
      setError(null);
      try {
        const bucketName = cachedBucket || DEFAULT_BUCKET;
        await supabase.storage.from(bucketName).remove([oldPath]);

        const fileName = `${Date.now()}-${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
        const path = oldPath.split('/')[0] + '/' + fileName;

        const { error: uploadError } = await supabase.storage
          .from(bucketName)
          .upload(path, file, {
            cacheControl: '3600',
            upsert: false,
          });
        if (uploadError) throw uploadError;

        const {
          data: { publicUrl },
        } = supabase.storage.from(bucketName).getPublicUrl(path);

        const { data: updated, error: dbError } = await supabase
          .from('assets_library')
          .update({
            name: name || file.name,
            storage_path: path,
            public_url: publicUrl,
            size_bytes: file.size,
            mime_type: file.type,
            updated_at: new Date().toISOString(),
          })
          .eq('id', id)
          .select()
          .single();

        if (dbError) throw dbError;

        return updated as Asset;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao substituir asset');
        showToast('Erro ao substituir imagem: ' + (err instanceof Error ? err.message : 'Erro desconhecido'), 'error');
        return null;
      } finally {
        setUploading(false);
      }
    },
    []
  );

  return {
    uploadImage,
    uploadImageByUrl,
    listAssets,
    deleteAsset,
    replaceAsset,
    uploading,
    error,
  };
}